import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ============================================================
// PRODUCTION CONSOLE FILTER
// Suppress console.log and console.debug in production builds.
// Errors and warnings are always preserved.
// ============================================================
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
}

// ============================================================
// GLOBAL IST TIMEZONE OVERRIDE
// Forces ALL date/time formatting to use Asia/Kolkata (IST).
// Also fixes naive UTC timestamps from backend (no Z suffix)
// so JavaScript correctly interprets them as UTC before
// converting to IST for display.
// ============================================================
const IST = 'Asia/Kolkata';

// Fix: Backend sends naive UTC strings like "2026-03-02T18:24:50.831"
// without 'Z' or '+00:00'. JS treats these as LOCAL time (wrong!).
// We patch the Date constructor to append 'Z' to such strings.
const _OrigDate = Date;
const _origDateParse = Date.parse;

// Helper: checks if a string is an ISO-like datetime without timezone
function isNaiveISOString(s) {
  if (typeof s !== 'string') return false;
  // Match patterns like "2026-03-02T18:24:50" or "2026-03-02T18:24:50.831683"
  // but NOT ones that already have Z, +00:00, +05:30 etc.
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[Zz]|\+\d{2}:\d{2}|-\d{2}:\d{2}$/.test(s);
}

// Patch Date constructor to fix naive UTC strings
// IMPORTANT: Use a proper class extension to preserve instanceof checks
// This is required for jsPDF and other libraries that use instanceof Date
class PatchedDate extends _OrigDate {
  constructor(...args) {
    if (args.length === 1 && isNaiveISOString(args[0])) {
      super(args[0] + 'Z'); // Treat as UTC
    } else {
      super(...args);
    }
  }
}

// Copy all static methods from original Date
Object.getOwnPropertyNames(_OrigDate).forEach(prop => {
  if (prop !== 'prototype' && prop !== 'length' && prop !== 'name') {
    try {
      PatchedDate[prop] = _OrigDate[prop];
    } catch (e) { /* ignore non-configurable */ }
  }
});

// Override Date.parse to also fix naive strings
PatchedDate.parse = function(s) {
  if (isNaiveISOString(s)) return _origDateParse(s + 'Z');
  return _origDateParse(s);
};

window.Date = PatchedDate;

// Patch toLocale* methods to always use IST
const _origToLocaleDateString = _OrigDate.prototype.toLocaleDateString;
_OrigDate.prototype.toLocaleDateString = function(locale, options) {
  const opts = { timeZone: IST, ...(options || {}) };
  return _origToLocaleDateString.call(this, locale || 'en-IN', opts);
};

const _origToLocaleTimeString = _OrigDate.prototype.toLocaleTimeString;
_OrigDate.prototype.toLocaleTimeString = function(locale, options) {
  const opts = { timeZone: IST, ...(options || {}) };
  return _origToLocaleTimeString.call(this, locale || 'en-IN', opts);
};

const _origToLocaleString = _OrigDate.prototype.toLocaleString;
_OrigDate.prototype.toLocaleString = function(locale, options) {
  const opts = { timeZone: IST, ...(options || {}) };
  return _origToLocaleString.call(this, locale || 'en-IN', opts);
};
// ============================================================

// Suppress ResizeObserver loop error (benign error from Radix UI/shadcn)
// This is a known non-critical error that doesn't affect functionality
const RESIZE_OBSERVER_ERROR_MSG = 'ResizeObserver loop completed with undelivered notifications';

// Patch ResizeObserver to suppress the error at source
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      // Wrap callback in requestAnimationFrame to avoid the loop error
      window.requestAnimationFrame(() => {
        try {
          callback(entries, observer);
        } catch (e) {
          // Suppress ResizeObserver errors silently
        }
      });
    });
  }
};

// Global error handler
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return true;
  }
}, true);

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop')) {
    e.preventDefault();
    return true;
  }
});

// Override console.error to suppress ResizeObserver errors from React
const originalConsoleError = console.error;
console.error = (...args) => {
  if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
